import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Controller, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Percent } from '@mui/icons-material';
import useToastr from '../../hooks/useToastr';
import {
  getAffiliateCommissonDetailById,
  updateAffiliateCommissonDetailById,
} from '../../services/Affiliates';
import Validations from '../../utils/Validations';

const ManageCommissionFormDialog = ({ onClose, onSave, editId }) => {
  const { showErrorToastr, showSuccessToastr } = useToastr();
  const { control, handleSubmit, setValue } = useForm();
  const [processing, setProcessing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [currentPromoCode, setCurrentPromocode] = useState('');
  const onSubmit = async (data) => {
    try {
      setProcessing(true);
      await updateAffiliateCommissonDetailById(editId, {
        commission: data.commission,
        currentPromoCode,
      });
      showSuccessToastr('Affiliate details updated successfully.');
      onSave();
    } catch (error) {
      showErrorToastr(error?.response?.data?.message || error?.message || 'Something went wrong.');
      setProcessing(false);
      onClose();
    }
  };

  useEffect(() => {
    setDataLoaded(false);

    if (editId) {
      getAffiliateCommissonDetailById(editId)
        .then((r) => {
          setCurrentPromocode(r?.promocode ? r.promocode : '');
          setValue('commission', r?.commission === 0 ? 0 : r?.commission);
          setDataLoaded(true);
        })
        .catch((e) => {
          showErrorToastr(e?.message || 'Something went wrong.');
        });
    }
  }, [editId]);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Update Affiliate Details</DialogTitle>
      <DialogContent>
        {dataLoaded ? (
          <form id="manage-commission" onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column">
              <Controller
                control={control}
                id="commission"
                name="commission"
                rules={{ ...Validations.REQUIRED }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextField
                    margin="dense"
                    label="Commission"
                    variant="outlined"
                    type="number"
                    sx={{ mt: 2 }}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error?.message : null}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Percent />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </form>
        ) : (
          <Box
            minWidth="200px"
            height="200px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress color="primary" size={24} sx={{ mr: 1 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          type="submit"
          form="manage-commission"
          endIcon={processing && <CircularProgress size={25} />}
          disabled={processing}
          color="primary"
          variant="contained"
          disableElevation
        >
          Update
        </Button>
        <Button onClick={onClose} variant="contained" disableElevation>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageCommissionFormDialog;

ManageCommissionFormDialog.propTypes = {
  editId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
